/**
 * GLSL port of SliceRenderingShader.shader, CT window/level mode. The fragment maps its
 * position into the volume's uvw through the inverse volume matrix; the optional label overlay
 * (the run's overlay volume with a colour LUT) replaces the desktop's secondary-texture path.
 */

export const sliceVertexShader = /* glsl */ `
out vec3 vWorld;
void main() {
  vec4 w = modelMatrix * vec4(position, 1.0);
  vWorld = w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}
`;

export const sliceFragmentShader = /* glsl */ `
precision highp float;
precision highp sampler3D;

in vec3 vWorld;
out vec4 fragColor;

uniform sampler3D uData;
uniform mat4 uVolumeInverse;      // world -> cube-local (±0.5)
uniform float uMinVal;
uniform float uMaxVal;
uniform float uGain;
uniform float uContrast;
uniform float uAlpha;
uniform float uUseFull;
uniform sampler3D uFullData;
uniform vec3 uFullUvwMin;
uniform vec3 uFullUvwScale;
uniform float uUseLabels;
uniform sampler3D uLabels;
uniform sampler2D uLabelLut;      // 256 x 1, colour per label id, alpha = blend
uniform float uLeafletEnabled;
uniform sampler3D uCusp;
uniform float uCuspDistMaxMm;
uniform float uCuspT0;
uniform float uCuspT1;
uniform vec4 uCuspPlane;
uniform float uLeafletFill;
uniform vec3 uStepUvw;

vec3 srgbToLinear(vec3 c) {
  return c * (c * (c * 0.305306011 + 0.682171111) + 0.012522878);
}

void main() {
  vec3 dataCoord = (uVolumeInverse * vec4(vWorld, 1.0)).xyz + vec3(0.5);
  bool insideCropped = !(any(greaterThan(dataCoord, vec3(1.0))) || any(lessThan(dataCoord, vec3(0.0))));
  float dataVal;
  if (uUseFull > 0.5) {
    vec3 fullCoord = uFullUvwMin + dataCoord * uFullUvwScale;
    if (any(greaterThan(fullCoord, vec3(1.0))) || any(lessThan(fullCoord, vec3(0.0)))) {
      fragColor = vec4(0.0, 0.0, 0.0, 1.0);
      return;
    }
    dataVal = texture(uFullData, fullCoord).r;
  } else {
    if (!insideCropped) {
      fragColor = vec4(0.0, 0.0, 0.0, 1.0);
      return;
    }
    dataVal = texture(uData, dataCoord).r;
  }

  float windowWidth = max(uMaxVal - uMinVal, 0.001);
  float normalizedIntensity = clamp((dataVal - uMinVal) / windowWidth, 0.0, 1.0);
  float shade = clamp(normalizedIntensity + uGain, 0.0, 1.0);
  shade = clamp((shade - 0.5) * uContrast + 0.5, 0.0, 1.0);
  vec3 finalRgb = vec3(shade);
  float finalA = clamp(1.0 + uAlpha, 0.0, 1.0);

  if (uUseLabels > 0.5 && insideCropped) {
    float id = texture(uLabels, dataCoord).r;        // label / 255, nearest filtering
    vec4 lut = texture(uLabelLut, vec2(id, 0.5));
    if (lut.a > 0.0) finalRgb = mix(finalRgb, srgbToLinear(lut.rgb), clamp(lut.a, 0.0, 1.0));
  }

  float leafletA = 0.0;
  if (uLeafletEnabled > 0.5 && insideCropped) {
    vec4 cf = texture(uCusp, dataCoord);
    float defHole = 1.0 - smoothstep(0.2, 0.8, cf.a);
    float planeMm = dot(uCuspPlane.xyz, dataCoord) - uCuspPlane.w;
    float planeCut = 1.0 - smoothstep(0.0, 1.5, planeMm);
    vec3 axisMag = abs(cross(dFdx(dataCoord), dFdy(dataCoord)));
    vec3 stepUvw = uStepUvw * (axisMag.x > max(axisMag.y, axisMag.z) ? vec3(1.0, 0.0, 0.0)
      : (axisMag.y > axisMag.z ? vec3(0.0, 1.0, 0.0) : vec3(0.0, 0.0, 1.0)));
    float uMin = min(cf.r, min(cf.g, cf.b));
    for (int tap = -2; tap <= 2; tap++) {
      if (tap == 0) continue;
      vec4 tf = texture(uCusp, dataCoord + stepUvw * float(tap));
      uMin = min(uMin, min(tf.r, min(tf.g, tf.b)));
    }
    float surf = 1.0 - smoothstep(uCuspT0, uCuspT1, uMin * uCuspDistMaxMm);
    surf *= min(uLeafletFill, 1.0);
    leafletA = clamp(surf * defHole * planeCut, 0.0, 1.0) * 0.65;
    if (leafletA < 0.02) leafletA = 0.0;
  }
  finalRgb = mix(finalRgb, vec3(1.0, 0.12, 0.12), leafletA);
  finalA = max(finalA, leafletA);
  fragColor = linearToOutputTexel(vec4(finalRgb, finalA));
}
`;
